import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Table } from 'react-bootstrap';
import { format, parse } from 'date-fns';
import {
  MdDelete,
  MdEdit,
  MdKeyboardArrowRight,
  MdOutlineAddBox
} from 'react-icons/md';
import { debounce } from 'lodash';
import TupleLink from '../TupleLink';
import TableResponsiveCard from '../TableResponsiveCard';
import CardDetailsModal from '../CardDetailsModal';
import ActionColumn from 'screens/common/components/ActionColumn';
import DynamicModal from 'screens/common/DynamicCollapseReport/DynamicModal';

const ServerSideTable = ({
  getTableProps,
  headers,
  page,
  prepareRow,
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
  filters,
  handleFilterChange,
  showFilterRow,
  cursor,
  clientSideSearch,
  handleAction, // New prop to handle actions
  responsive,
  cardTittles,
  selectedRowIds,
  onSelectionChange
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close modal
    setSelectedRowData(null); // Clear selected row data
  };

  const latestSelectionChange = React.useRef(onSelectionChange);
  React.useLayoutEffect(() => {
    latestSelectionChange.current = onSelectionChange;
  }, [onSelectionChange]);

  React.useEffect(() => {
    if (
      latestSelectionChange.current &&
      typeof latestSelectionChange.current === 'function'
    ) {
      latestSelectionChange.current(selectedRowIds || {});
    }
  }, [selectedRowIds]);

  const debouncedHandleFilterChange = useMemo(
    () => debounce(handleFilterChange, 100),
    [handleFilterChange]
  );

  const onFilterChange = (columnId, value) => {
    if (clientSideSearch) {
      handleFilterChange(columnId, value);
    } else {
      debouncedHandleFilterChange(columnId, value);
    }
  };

  const renderCollapsibleContent = data => {
    if (!data || typeof data !== 'object') return null;

    return (
      <div className="p-3 p-md-4 bg-light border-top border-bottom">
        {data.title && (
          <p className="text-uppercase fw-bold mb-3 text-700 fs--1">
            {data.title}
          </p>
        )}
        {Array.isArray(data.data) ? (
          <div className="table-responsive scrollbar">
            <Table
              bordered
              striped
              size="sm"
              className="fs--1 mb-0 bg-white"
              style={{ minWidth: '600px' }}
            >
              <thead className="bg-200 text-900">
                <tr>
                  {Object.keys(data.data[0] || {}).map((key, index) => (
                    <th key={index} className="align-middle white-space-nowrap">
                      {key
                        .replace(/_/g, ' ')
                        .replace(/(^\w|\b\w(?![^()]*\)))/g, c =>
                          c.toUpperCase()
                        )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.data.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Object.values(row).map((value, cellIndex) => (
                      <td
                        key={cellIndex}
                        className="align-middle py-2 white-space-nowrap"
                      >
                        {value === null || value === undefined ? '-' : value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="row g-3">
            {Object.entries(data.data || {}).map(
              ([subKey, subValue], index) => {
                const text = Array.isArray(subValue) ? subValue[0] : subValue;
                const color =
                  Array.isArray(subValue) && subValue[1]
                    ? subValue[1]
                    : 'inherit';

                return (
                  <div key={index} className="col-12 col-md-4 col-lg-3">
                    <div className="d-flex flex-column">
                      <span className="text-500 fw-semi-bold fs--2 text-uppercase mb-1">
                        {subKey}
                      </span>
                      <span className="text-800 fs--1" style={{ color }}>
                        {text || '-'}
                      </span>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        )}
      </div>
    );
  };

  const styles = `
    .expand-icon {
      transition: transform 0.3s ease;
    }

    .expand-icon.expanded {
      transform: rotate(90deg);
    }

    .expanded-content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease-in-out;
    }

    .expanded-content.show {
      max-height: 500px;
    }
    
    .truncate-cell {
      // max-width: 250px; /* Set max width */
      // overflow: hidden;
      // text-overflow: ellipsis;
      // white-space: nowrap;
    }
  `;

  return (
    <>
      <div
        className={`projects-table-wrapper ${
          responsive ? 'd-none d-md-block' : ''
        }`}
        style={{ overflowX: 'auto' }}
      >
        <style>{styles}</style>
        <Table {...getTableProps(tableProps)}>
          <thead className={headerClassName}>
            <tr>
              {page.some(row => row.original.more) && (
                <th style={{ width: 40 }}></th>
              )}
              {headers.map((column, index) => {
                const headerText =
                  typeof column.Header === 'string' ? column.Header : '';
                if (headerText.split(' ')[0] !== 'Section') {
                  //because section need to be avoided
                  return (
                    <th
                      key={index}
                      {...column.getHeaderProps(
                        column.getSortByToggleProps?.(column.headerProps)
                      )}
                      style={{
                        ...(column.isSticky
                          ? {
                              position: 'sticky',
                              left: `${column.stickyOffset}px`,
                              zIndex: 2,
                              backgroundColor: '#F2EFEA',
                              boxShadow: 'none'
                            }
                          : {})
                      }}
                    >
                      {column.render('Header')}
                      {column.canSort && (
                        <span
                          className={`sort ${
                            column.isSorted
                              ? column.isSortedDesc
                                ? 'desc'
                                : 'asc'
                              : ''
                          }`}
                        />
                      )}
                    </th>
                  );
                }
              })}
            </tr>
            {showFilterRow && (
              <tr>
                {page.some(row => row.original.more) && (
                  <th style={{ width: 40 }}></th>
                )}
                {headers.map((column, index) => {
                  const headerText =
                    typeof column.Header === 'string' ? column.Header : '';
                  if (headerText.split(' ')[0] !== 'Section') {
                    return (
                      <th
                        key={index}
                        style={{
                          ...(column.isSticky
                            ? {
                                position: 'sticky',
                                left: `${column.stickyOffset}px`,
                                zIndex: 2,
                                backgroundColor: '#F2EFEA'
                              }
                            : {})
                        }}
                      >
                        {headerText !== 'S No' &&
                          headerText !== 'Action' &&
                          headerText !== '' &&
                          (headerText.toLowerCase().includes('date') ||
                          [
                            'created on',
                            'modified on',
                            'transferred on'
                          ].includes(headerText.toLowerCase()) ? (
                            <input
                              type="date" // Render a date input field
                              //placeholder="Select Date"
                              value={
                                filters[column.id]
                                  ? format(
                                      parse(
                                        filters[column.id],
                                        'dd-MM-yyyy',
                                        new Date()
                                      ), // Convert stored value to ISO format
                                      'yyyy-MM-dd'
                                    ) // Convert stored value to ISO format
                                  : ''
                              }
                              onChange={e => {
                                const value = e.target.value;
                                if (value) {
                                  const formattedDate = format(
                                    new Date(value),
                                    'dd-MM-yyyy'
                                  );
                                  onFilterChange(column.id, formattedDate);
                                } else {
                                  onFilterChange(column.id, '');
                                }
                              }}
                              max={format(
                                new Date(
                                  new Date().setDate(new Date().getDate() + 6)
                                ),
                                'yyyy-MM-dd'
                              )}
                              className="w-full p-1 border rounded"
                            />
                          ) : (
                            <input
                              type="text"
                              placeholder="Search"
                              value={filters[column.id] || ''}
                              onChange={e => {
                                onFilterChange(column.id, e.target.value);
                              }}
                              className="w-full p-1 border rounded"
                            />
                          ))}
                      </th>
                    );
                  }
                })}
              </tr>
            )}
          </thead>
          <tbody className={bodyClassName}>
            {page.map((row, i) => {
              prepareRow(row);
              const hasMoreData =
                row.original.more && Object.keys(row.original.more).length > 0;
              const isExpanded = expandedRow === i;

              return (
                <React.Fragment key={i}>
                  <tr {...row.getRowProps()} className={rowClassName}>
                    {hasMoreData && (
                      <td
                        className="w-10 cursor-pointer"
                        onClick={() => setExpandedRow(isExpanded ? null : i)}
                      >
                        <div
                          className={`expand-icon ${
                            isExpanded ? 'expanded' : ''
                          }`}
                        >
                          <MdKeyboardArrowRight size={20} />
                        </div>
                      </td>
                    )}

                    {row.cells.map((cell, cellIndex) => {
                      const cellHeaderText =
                        typeof cell.column.Header === 'string'
                          ? cell.column.Header
                          : '';
                      // Special handling for 'S No' column
                      if (cellHeaderText.split(' ')[0] == 'Section') {
                        return null;
                      }
                      if (cellHeaderText === 'S No') {
                        const sNo = cursor + i + 1;
                        return (
                          <td
                            key={cellIndex}
                            {...cell.getCellProps()}
                            style={{
                              ...(cell.column.isSticky
                                ? {
                                    position: 'sticky',
                                    left: `${cell.column.stickyOffset}px`,
                                    zIndex: 1,
                                    backgroundColor: 'inherit'
                                  }
                                : {})
                            }}
                          >
                            {sNo}
                          </td>
                        );
                      }

                      // Special handling for 'Severity' column
                      if (
                        cellHeaderText === 'Severity' &&
                        typeof cell.value === 'object'
                      ) {
                        return (
                          <td
                            key={cellIndex}
                            {...cell.getCellProps()}
                            style={{
                              ...(cell.column.isSticky
                                ? {
                                    position: 'sticky',
                                    left: `${cell.column.stickyOffset}px`,
                                    zIndex: 1,
                                    backgroundColor: 'inherit'
                                  }
                                : {})
                            }}
                          >
                            <span style={{ color: cell.value.color }}>
                              {cell.value.label}
                            </span>
                          </td>
                        );
                      }

                      // Special handling for columns with an array of strings (e.g., 'risk_ranges')
                      if (
                        Array.isArray(cell.value) &&
                        typeof cell.value[0] === 'string'
                      ) {
                        return (
                          <td
                            key={cellIndex}
                            {...cell.getCellProps()}
                            style={{
                              ...(cell.column.isSticky
                                ? {
                                    position: 'sticky',
                                    left: `${cell.column.stickyOffset}px`,
                                    zIndex: 1,
                                    backgroundColor: 'inherit'
                                  }
                                : {})
                            }}
                          >
                            {/* Map through the array of strings and render each on a new line */}
                            {cell.value.map((item, index) => (
                              <div key={index}>{item}</div>
                            ))}
                          </td>
                        );
                      }

                      // Special handling for 'Action' column
                      if (cellHeaderText == 'Action') {
                        // Get cell props but remove the key
                        const cellProps = cell.getCellProps();
                        delete cellProps.key;

                        return (
                          <ActionColumn
                            key={cellIndex}
                            row={row}
                            handleAction={handleAction}
                            cellProps={{
                              ...cellProps,
                              style: {
                                ...(cell.column.isSticky
                                  ? {
                                      position: 'sticky',
                                      left: `${cell.column.stickyOffset}px`,
                                      zIndex: 1,
                                      backgroundColor: 'inherit'
                                    }
                                  : {})
                              }
                            }}
                          />
                        );
                      }

                      // Default handling for all other columns
                      return (
                        <td
                          key={cellIndex}
                          {...cell.getCellProps()}
                          style={{
                            ...(cell.column.isSticky
                              ? {
                                  position: 'sticky',
                                  left: `${cell.column.stickyOffset}px`,
                                  zIndex: 1,
                                  backgroundColor: 'inherit'
                                }
                              : {})
                          }}
                        >
                          <div
                            className="truncate-cell"
                            style={{
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={cell.value}
                          >
                            {cell.value === 0 ? '0' : cell.render('Cell')}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {hasMoreData && (
                    <tr className={rowClassName}>
                      <td
                        colSpan={headers.length + (hasMoreData ? 1 : 0)}
                        className="p-0"
                      >
                        <div
                          className={`expanded-content ${
                            isExpanded ? 'show' : ''
                          }`}
                        >
                          {renderCollapsibleContent(row.original.more)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
      {/*the responsive card section that works only in smaller screen*/}
      {responsive && (
        <TableResponsiveCard
          page={page}
          cardTittles={cardTittles}
          setIsModalOpen={setIsModalOpen}
          setSelectedRowData={setSelectedRowData}
          handleAction={handleAction}
          renderCollapsibleContent={renderCollapsibleContent}
          prepareRow={prepareRow}
          expandedRow={expandedRow}
        />
      )}

      {/* Modal */}
      {/* {isModalOpen && selectedRowData && (
        <CardDetailsModal
          selectedRowData={selectedRowData}
          cardTittles={cardTittles}
          handleCloseModal={handleCloseModal}
          handleAction={handleAction}
        />
      )} */}
      {/* {isModalOpen && selectedRowData && (
        <DynamicModal
          show={true}
          onClose={() => handleCloseModal()}
          selectedModalData={selectedRowData}
        />
      )} */}
    </>
  );
};

ServerSideTable.propTypes = {
  getTableProps: PropTypes.func,
  headers: PropTypes.array,
  page: PropTypes.array,
  prepareRow: PropTypes.func,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  rowClassName: PropTypes.string,
  tableProps: PropTypes.object,
  filters: PropTypes.object,
  handleFilterChange: PropTypes.func,
  showFilterRow: PropTypes.bool,
  cursor: PropTypes.number,
  clientSideSearch: PropTypes.bool,
  handleAction: PropTypes.func, // New prop to handle actions
  responsive: PropTypes.bool,
  cardTittles: PropTypes.arrayOf(PropTypes.string),
  selectedRowIds: PropTypes.object,
  onSelectionChange: PropTypes.func
};

export default ServerSideTable;
